import { useEffect, useRef } from 'react';
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts';
import { formatCryptoPrice } from '@/lib/format';
import { ColorType, CrosshairMode, LineStyle, PriceScaleMode, TrackingModeExitMode } from 'lightweight-charts';

export const useChart = (containerRef: React.RefObject<HTMLDivElement>) => {
  const chartRef = useRef<IChartApi>();
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'>>();
  // const volumeSeriesRef = useRef<ISeriesApi<'Histogram'>>();

  const scrollToRealTime = () => {
    if (!chartRef.current || !candlestickSeriesRef.current) return;

    const data = candlestickSeriesRef.current.data();
    if (data.length > 0) {
      chartRef.current.timeScale().setVisibleLogicalRange({
        from: Math.max(0, data.length - 50),
        to: data.length,
      });
    }
  };

  useEffect(() => {
    if (!containerRef.current || chartRef.current) return;

    const chart = createChart(containerRef.current, {
      leftPriceScale: {
        visible: false,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      width: 0,
      height: 0,
      localization: {
        locale: 'zh-CN',
        dateFormat: 'yyyy-MM-dd',
      },
      layout: {
        background: { type: ColorType.Solid, color: '' }, // 背景配置：类型和颜色 #18181b
        textColor: '#353535', // 文本颜色
        attributionLogo: false, // 是否显示 TradingView logo
        fontFamily: 'popping', // 字体
        fontSize: 12, // 字号
      },
      grid: {
        vertLines: {
          color: '', // 垂直网格线颜色
          style: LineStyle.Solid, // 线条样式：实线
          visible: false, // 是否显示
        },
        horzLines: {
          color: '', // 垂直网格线颜色
          style: LineStyle.Solid, // 线条样式：实线
          visible: true, // 是否显示
        },
      },
      watermark: {
        text: 'BOOM.MEME', // 水印文字
        color: '#353535', // 水印颜色
        fontSize: 35, // 字号
        visible: true, // 是否显示
        fontFamily: 'Arial', // 字体
        fontStyle: 'bold', // 字体样式
        horzAlign: 'center', // 水平对齐方式
        vertAlign: 'center', // 垂直对齐方式
      },
      // 时间轴
      timeScale: {
        tickMarkFormatter: (time: any) => {
          const date = new Date(parseInt(time) * 1000);
          const today = new Date();
          const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
          if (isToday) {
            // 当天只显示时间
            return date.toLocaleString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
            });
          } else {
            // 非当天显示日期和时间
            return date.toLocaleString('zh-CN', {
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              // 如果跨年了，加上 year 选项
              year: 'numeric',
            });
          }
        },
        rightOffset: 0, // 右侧偏移量
        barSpacing: 0, // K线柱间距
        fixLeftEdge: false, // 固定左边缘
        fixRightEdge: false, // 添加这个属性
        lockVisibleTimeRangeOnResize: true, // 调整大小时锁定可见时间范围
        rightBarStaysOnScroll: true, // 滚动时右侧K线保持固定
        borderVisible: false, // 是否显示边框
        visible: true, // 是否显示时间轴
        timeVisible: true, // 是否显示时间
        secondsVisible: false, // 是否显示秒
        minBarSpacing: 0, // 最小K线间距
        borderColor: '#ccc', // 添加这个属性
        shiftVisibleRangeOnNewBar: false, // 添加这个属性
        allowShiftVisibleRangeOnWhitespaceReplacement: false, // 添加这个属性
        ticksVisible: true, // 添加这个属性
        uniformDistribution: true, // 添加这个属性
        minimumHeight: 0, // 添加这个属性
        allowBoldLabels: true, // 添加这个属性
      },
      // 价格轴
      rightPriceScale: {
        autoScale: true, // 自动缩放
        mode: PriceScaleMode.Normal, // 价格轴模式：普通
        invertScale: false, // 反转刻度
        alignLabels: true, // 对齐标签
        scaleMargins: {
          top: 0.1, // 顶部边距
          bottom: 0.1, // 底部边距
        },
        borderVisible: false, // 显示边框
        borderColor: '', // 边框颜色
        entireTextOnly: false, // 仅显示完整文本
        visible: true, // 是否显示
        ticksVisible: false, // 显示刻度
        minimumWidth: 0, // 最小宽度
      },
      handleScroll: true, // 允许滚动
      handleScale: true, // 允许缩放
      autoSize: true, // 自动调整大小
      kineticScroll: {
        touch: true, // 触摸滚动
        mouse: true, // 鼠标滚动
      },
      // 跟踪模式
      trackingMode: {
        exitMode: TrackingModeExitMode.OnNextTap, // 退出模式：下次点击时
      },
    });
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#16C782',
      downColor: '#EA3943',
      borderDownColor: '#EA3943',
      borderUpColor: '#16C782',
      wickUpColor: '#16C782',
      wickDownColor: '#EA3943',
      priceFormat: {
        type: 'custom',
        minMove: 0.00000000000000001, // 放大倍数
        formatter: (price: number) => formatCryptoPrice(price),
      },
    });
    // const volumeSeries = chart.addHistogramSeries({
    //   priceFormat: {
    //     type: 'volume',
    //     precision: 1, // 设置精度
    //     minMove: 0.001, // 设置最小移动单位
    //   },
    //   lastValueVisible: true, // 显示最新价格标签
    //   priceScaleId: '', // 独立的价格轴
    //   visible: true,
    //   priceLineVisible: false, // 不显示价格线
    //   baseLineVisible: true, // 不显示基准线
    //   priceLineWidth: 1,
    // });

    candlestickSeriesRef.current = candlestickSeries;
    // volumeSeriesRef.current = volumeSeries;
    chartRef.current = chart;

    return () => {
      chart.remove();
    };
  }, []);

  return {
    chart: chartRef.current,
    candlestickSeries: candlestickSeriesRef.current,
    // volumeSeries: volumeSeriesRef.current,
    scrollToRealTime,
  };
};
